import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

import Card from "components/card/Card";
import dashboardRepository, {
  AdminDashboardResponse,
} from "api/dashboardRepository";

export default function Dashboard() {
  const pageBg = useColorModeValue("#F3F5FE", "#0B1437");
  const chartBg = useColorModeValue("#FFFFFF", "#111C44");
  const chartBorder = useColorModeValue("#EEF0F7", "whiteAlpha.200");
  const chartTitle = useColorModeValue("#252B42", "white");
  const chartMuted = useColorModeValue("#9298AA", "whiteAlpha.700");
  const divider = useColorModeValue("#EDF0F6", "whiteAlpha.200");
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<AdminDashboardResponse | null>(null);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setDashboard(await dashboardRepository.getAdminDashboard());
    } catch (loadError) {
      console.error("Unable to load admin dashboard:", loadError);
      setError("Unable to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  if (loading) {
    return (
      <Flex minH="80vh" justify="center" align="center" direction="column" bg={pageBg}>
        <Spinner size="xl" color="#7062B9" />
        <Text mt={5} color={chartMuted} fontWeight="500">Loading Dashboard...</Text>
      </Flex>
    );
  }

  if (!dashboard || error) {
    return (
      <Flex minH="80vh" justify="center" align="center" direction="column" bg={pageBg} px={4}>
        <Alert status="error" maxW="520px" borderRadius="16px">
          <AlertIcon />
          {error || "Dashboard data is unavailable."}
        </Alert>
        <Button mt={4} bg="#7062B9" color="white" onClick={() => void loadDashboard()}>
          Try Again
        </Button>
      </Flex>
    );
  }

  const createBarOptions = (categories: string[], colors: string[]): ApexOptions => ({
    chart: {
      type: "bar",
      toolbar: { show: false },
      fontFamily: "inherit",
      background: "transparent",
    },
    plotOptions: {
      bar: { distributed: true, borderRadius: 7, columnWidth: "42%" },
    },
    colors,
    dataLabels: {
      enabled: true,
      style: { fontSize: "13px", fontWeight: 600 },
    },
    grid: { borderColor: divider, strokeDashArray: 4 },
    xaxis: {
      categories,
      labels: { style: { colors: chartMuted, fontSize: "12px" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: { colors: chartMuted, fontSize: "12px" } } },
    legend: { show: false },
    tooltip: { theme: "light" },
  });

  const chartCardStyle = {
    bg: chartBg,
    borderRadius: "18px",
    boxShadow: "0px 8px 25px rgba(63, 81, 181, 0.08)",
    border: "1px solid",
    borderColor: chartBorder,
  };

  const customerAmcOptions: ApexOptions = {
    labels: ["AMC not expiring", "AMC expiring within a month", "Without AMC"],
    colors: ["#36B6A7", "#F3A83B", "#EF7777"],
    chart: { fontFamily: "inherit" },
    stroke: { width: 4, colors: [chartBg] },
    legend: {
      position: "bottom",
      fontSize: "12px",
      labels: { colors: chartMuted },
    },
    dataLabels: { enabled: true, style: { fontSize: "13px", fontWeight: 600 } },
    plotOptions: {
      pie: {
        donut: {
          size: "68%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Customers",
              color: chartMuted,
              formatter: () => String(dashboard.customers.total),
            },
          },
        },
      },
    },
    tooltip: { theme: "light" },
  };

  return (
    <Box minH="100vh" bg={pageBg} pt={{ base: "80px", md: "90px" }} px={{ base: 4, md: 7 }} pb={10}>
      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="20px" mb="20px">
        <Card p={{ base: "18px", md: "22px" }} {...chartCardStyle}>
          <Flex justify="space-between" align="center" mb={4}>
            <Box>
              <Text fontWeight="700" fontSize="17px" color={chartTitle}>Customer AMC Status</Text>
              <Text fontSize="12px" color={chartMuted} mt="3px">AMC coverage and upcoming expirations</Text>
            </Box>
            <Badge px="10px" py="5px" borderRadius="8px" bg="#F0F4FF" color="#5572D8" fontSize="11px">
              Customers
            </Badge>
          </Flex>
          <Chart
            type="donut"
            height={290}
            options={customerAmcOptions}
            series={[
              Math.max(
                dashboard.customers.withAmc -
                  dashboard.customers.expiringAmcWithinMonth,
                0
              ),
              dashboard.customers.expiringAmcWithinMonth,
              dashboard.customers.withoutAmc,
            ]}
          />
        </Card>

        <Card p={{ base: "18px", md: "22px" }} {...chartCardStyle}>
          <Flex justify="space-between" align="center" mb={4}>
            <Box>
              <Text fontWeight="700" fontSize="17px" color={chartTitle}>Service Call Status</Text>
              <Text fontSize="12px" color={chartMuted} mt="3px">Current service call overview</Text>
            </Box>
            <Badge px="10px" py="5px" borderRadius="8px" bg="#F3F1FF" color="#735BC7" fontSize="11px">
              Calls
            </Badge>
          </Flex>
          <Chart
            type="bar"
            height={290}
            options={createBarOptions(["Open", "In Progress", "Closed"], ["#EF7777", "#F3A83B", "#35B99A"])}
            series={[{ data: [dashboard.serviceCalls.open, dashboard.serviceCalls.inProgress, dashboard.serviceCalls.closed] }]}
          />
        </Card>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="20px">
        <Card p={{ base: "18px", md: "22px" }} {...chartCardStyle}>
          <Text fontWeight="700" fontSize="17px" color={chartTitle} mb="18px">Customer Summary</Text>
          <VStack spacing="0" align="stretch" divider={<Box borderBottom="1px solid" borderColor={divider} />}>
            <Flex justify="space-between" align="center" py="14px">
              <Text color={chartMuted} fontSize="14px">With AMC</Text>
              <Badge px="10px" py="5px" borderRadius="8px" bg="#E7F8F2" color="#219A78" fontSize="12px">
                {dashboard.customers.withAmc}
              </Badge>
            </Flex>
            <Flex justify="space-between" align="center" py="14px">
              <Text color={chartMuted} fontSize="14px">Without AMC</Text>
              <Badge px="10px" py="5px" borderRadius="8px" bg="#FDECEC" color="#D75B5B" fontSize="12px">
                {dashboard.customers.withoutAmc}
              </Badge>
            </Flex>
            <Flex justify="space-between" align="center" py="14px">
              <Text color={chartMuted} fontSize="14px">AMC expiring within a month</Text>
              <Badge px="10px" py="5px" borderRadius="8px" bg="#FFF4DD" color="#B7791F" fontSize="12px">
                {dashboard.customers.expiringAmcWithinMonth}
              </Badge>
            </Flex>
            <Flex justify="space-between" align="center" py="14px">
              <Text color={chartTitle} fontWeight="600" fontSize="14px">Total Customers</Text>
              <Badge px="10px" py="5px" borderRadius="8px" bg="#EEF3FF" color="#4D70D5" fontSize="12px">
                {dashboard.customers.total}
              </Badge>
            </Flex>
          </VStack>
        </Card>

        <Card p={{ base: "18px", md: "22px" }} {...chartCardStyle}>
          <Text fontWeight="700" fontSize="17px" color={chartTitle} mb="18px">Quotation Summary</Text>
          <VStack spacing="0" align="stretch" divider={<Box borderBottom="1px solid" borderColor={divider} />}>
            <Flex justify="space-between" align="center" py="14px">
              <Text color={chartTitle} fontWeight="600" fontSize="14px">Total Quotations</Text>
              <Badge px="10px" py="5px" borderRadius="8px" bg="#EEF3FF" color="#4D70D5" fontSize="12px">
                {dashboard.quotations.total}
              </Badge>
            </Flex>
            <Flex justify="space-between" align="center" py="14px">
              <Text color={chartMuted} fontSize="14px">Total Service Calls</Text>
              <Badge px="10px" py="5px" borderRadius="8px" bg="#F3F1FF" color="#735BC7" fontSize="12px">
                {dashboard.serviceCalls.total}
              </Badge>
            </Flex>
            <Flex justify="space-between" align="center" py="14px">
              <Text color={chartMuted} fontSize="14px">Closed Service Calls</Text>
              <Badge px="10px" py="5px" borderRadius="8px" bg="#E7F8F2" color="#219A78" fontSize="12px">
                {dashboard.serviceCalls.closed}
              </Badge>
            </Flex>
          </VStack>
        </Card>
      </SimpleGrid>
    </Box>
  );
}
